import LookupService from "./lookupService";
import syncService from "../services/syncService";
import { getDispatchFromConfig, sort } from "../utils/miscUtils";
import { evvRepository } from "../../db/evv";
import documentRepository from "../../db/documentRepository";
import api from "./api";
import { SERVICE_DOCUMENT_STATUS_INCOMPLETE, SERVICE_DOCUMENT_STATUS_SIGNED, SERVICE_DOCUMENT_STATUS_UNSIGNED } from "../../features/forms/serviceDocumentUtils";
import { documentCache } from "../../cache/slices/document/documentCache";
import { isGA } from "../utils/goalsAddressedUtils";
import { canProcess, SYNC_STATUS } from "../utils/syncUtils";
import { SYNC_STATE_DIRTY } from "../constants";

const SERVICE_DOCUMENT_WRITE = '/evv/write/document';
const PDF_READ = '/evv/read/documentPdf';
const EVV_READ_DOCUMENT = '/evv/read/evvdocuments';


class DocumentService extends LookupService{

    saveDocument = (document, config) => {  
        if (document) {
            const docId = (-1 * Date.now());
            if (!document.id) {
                document.id = docId;
            }

            if (!document.syncState) document.syncState = SYNC_STATE_DIRTY;

            this.updatingAppointmentDocument(document);
            const documents = [document];

            config = {  ...config, 'saveDocument': true };

            return this.save(documents, config)
                .then(() => {
                    const dispatch = getDispatchFromConfig(config);
                    dispatch(this.operationAdd(document));
                    return document;
                })
        }
    }

    deleteDocument = (document, config) => {
        return this.deleteByPrimaryKey(document.id)
            .then(() => {
                const dispatch = getDispatchFromConfig(config);
                dispatch(this.operationRemove(document));
            })
    }

    updateDocument = async (id, moduleData, signature, documentStatus, clientProgramId, statusSync) => {
        let documentData = {
            "moduleData": moduleData,
            "signature": signature,
            "documentStatus": documentStatus,
            "statusSync": statusSync
        }
        documentData.clientProgramId = clientProgramId;
        evvRepository?.evvDb?.document
            .where("id").equals(id)
            .modify(
                    documentData
            );
    }

    updateDocumentStatusSync = async (id, statusSync) => {
        await evvRepository?.evvDb?.document
            .where("id")
            .equals(id)
            .modify(doc => {
                doc.statusSync = statusSync;
                if (statusSync === SYNC_STATUS.ERROR && doc.syncState !== SYNC_STATE_DIRTY) {
                    doc.syncState = SYNC_STATE_DIRTY;
                }
            });
    };

    getServiceDoc = async(serviceDocumentId) => {
        return evvRepository?.evvDb?.serviceDocument?.where("setupId").equals(serviceDocumentId).toArray();
    }

    findDocumentById = (id) => {
        return evvRepository?.evvDb?.document?.where({id: id}).first().then(doc => doc)
    }

    getGaModulesToSave = (module, newModuleToSave) => {
        module?.fieldData?.forEach(fieldData => {
            if (fieldData?.goalAddressedDetail) {
                let addressedElements = fieldData?.goalAddressedDetail?.filter(ele => ele.isAddressed);
                if (addressedElements?.length > 0) {
                    let addressedGaDetails = [];
                    let fieldDataToSave = { ...fieldData };

                    addressedElements?.forEach((ele) => {
                        const {isAddressed, ...rest} = { ...ele };
                        let temp = { ...rest };
                        addressedGaDetails.push(temp);
                    });

                    fieldDataToSave.goalAddressedDetail = [...addressedGaDetails];
                    newModuleToSave.push(fieldDataToSave);
                }
            } else {
                newModuleToSave.push(fieldData);
            }
        });
        return newModuleToSave;
    }

    getImpactModulesToSave = (module, newModuleToSave) => {
        let newModule = {};
        let newFields = [];

        newModule.measureId = module.measureId;
        newModule.moduleType = module.moduleType;
        newModule.score = module.totalScore;
        newModule.notAssessed = module.notAssessed;
        newModule.subTotal = module.subTotal;

        module.fieldData.forEach(fieldData => {
            let newFieldData = {};
            newFieldData.key = fieldData.key;
            newFieldData.value = Array.isArray(fieldData.value) ? fieldData.value.map((item) => item && item.length !== 0 ? item : 'null').join(",") : fieldData.value;
            newFields.push(newFieldData);
        });
        newModule.fieldData = newFields;
        newModuleToSave.push(newModule);
        return newModuleToSave;
    }

    writeDocument = async (serviceDocument, modulesToSave) => {
        const notIncludedFields = ['loaded', 'dbOptions', 'nextClicked', 'isInitializing'];
        let newModuleToSave  = [];
        modulesToSave.forEach(module => {
            if (module.moduleType === "cf") {
                let newModule = {};
                newModule.moduleId = module.moduleId;
                newModule.moduleType = module.moduleType;
                let newFields = [];
                module.fieldData.forEach(fieldData => {
                    if (!notIncludedFields.includes(fieldData.key)){
                        let newFieldData = {};
                        newFieldData.key = fieldData.key;
                        newFieldData.value = Array.isArray(fieldData.value) ? fieldData.value.map((item) => item && item.length !== 0 ? item : 'null').join("|") : fieldData.value;
                        newFields.push(newFieldData);
                    }
                });
                newModule.fieldData = newFields;
                newModuleToSave.push(newModule);
            } else if (isGA(module)) {
                //Goals Addressed Module
                newModuleToSave = this.getGaModulesToSave(module, newModuleToSave);
            } else {
                //Impact Module
                newModuleToSave = this.getImpactModulesToSave(module, newModuleToSave);
            }
        });

        let signatureInfo = {...serviceDocument.signature};
        if (signatureInfo.nextStaffId) {
            signatureInfo.nextStaffId = signatureInfo.nextStaffId.staffId;
        }
        
        let parameters = {
            resources: [
                {
                    id: serviceDocument.status === SERVICE_DOCUMENT_STATUS_SIGNED ? serviceDocument.documentId : null,
                    serviceDocumentId: serviceDocument.serviceDocumentId,
                    appointmentId: serviceDocument.appointmentId,
                    clientId: serviceDocument.clientId,
                    clientProgramId: serviceDocument.clientProgramId < 0 ? null : serviceDocument.clientProgramId,
                    staffId: serviceDocument.staffId,
                    organizationId: serviceDocument.organizationId,
                    serviceDate: serviceDocument.serviceDate.substring(0,10),
                    fullySigned: serviceDocument.fullySigned,
                    moduleData: newModuleToSave,
                    signature: Object.keys(signatureInfo).length > 0 ? signatureInfo : undefined,
                    online: true
                }
            ]
        };
        return api.post(SERVICE_DOCUMENT_WRITE, { json: parameters })
            .then(response => {
                this.updatingAppointmentDocumentPostDB(response);
                if (response && response.errors && response.errors.length > 0) {
                    return Promise.reject(new Error(response.errors[0].message));
                }
                
                return Promise.resolve(response);
            })
            .catch(error => {
                const connectionLost = api.verifyIfConnectionLost(error);

                if (connectionLost){
                    return Promise.resolve(parameters);
                } else {
                    console.log("Error thrown while writing service document: ");
                    console.log(error);
                    return Promise.reject(error);
                }
            });
    }

    updateAppointmentDocument = async (oldAppointmentId, newActivityDetailId, newAppointmentId) => {
        await evvRepository?.evvDb?.document
            .where("appointmentId").equals(oldAppointmentId)
            .modify(doc => {
                doc.appointmentId = newActivityDetailId;
                doc.appointmentIdForQuery = newAppointmentId;
                
                if (doc.statusSync === SYNC_STATUS.ERROR) {
                    delete doc.statusSync;
                }
            });
    }

    checkHasServiceDoc = (appointment) => {
        return this.repository.loadAllByProperty('appointmentId', appointment.activityDetailId || appointment.appointmentId)
            .then(clientDocs => {
                if (clientDocs && clientDocs.length > 0){
                    return true;
                } else {
                    return false;
                }
            })
    }

    checkHasAttachedDoc = (appointment) => {
        return this.repository.loadAllByProperty('appointmentId', appointment.activityDetailId || appointment.appointmentId)
            .then(doc => {
                if (doc && doc.length > 0) {
                    return doc;
                }
            })
    }

    getUnsignedIncompleteDocs = () => {
        return evvRepository?.evvDb?.document
        .where("documentStatus").anyOf(SERVICE_DOCUMENT_STATUS_UNSIGNED, SERVICE_DOCUMENT_STATUS_INCOMPLETE)
        .toArray();
    }

    getDocsCollection = (client) => {
        return evvRepository?.evvDb?.document.where("clientId").equals(Number(client.clientId)).toArray();
    }

    saveNewDocument = (documentToSave, store, dispatch) => {
        documentToSave.syncState = "dirty";
        this.saveDocument(documentToSave, {store, doNotClear: true})
            .then(savedDocument => {
                console.log('created a new document with an appointment');
                console.log(savedDocument);
                dispatch(documentCache.setCurrentDocumentId(savedDocument.id));
            })
            .catch(error => {
                console.log('Document can not be created with an appointment');
                console.log(error);
            });
    }

    getPdfUrl = async (documentId, force) => {
        let parameters = {
            documentId: documentId
        };

        if (force) {
            parameters.force = true;
        }

        return api.post(PDF_READ, { json: parameters })
            .then(response => {
                return Promise.resolve(response);
            })
            .catch(error => {
                const connectionLost = api.isConnectionLost(error);
                if (connectionLost){
                    return Promise.resolve({connectionLost: true});
                } else {
                    console.log("Error thrown while reading pdf document: ");
                    console.log(error);
                    return Promise.reject(error);
                }
            });
    }

    addPdfUrl = async (documentId, pdfUrl) => {
        evvRepository?.evvDb?.document
            .where("id").equals(documentId)
            .modify({
                "pdfUrl": pdfUrl
            });
    }
    
    readDocument = async (documentId) => {
        let parameters = {};
        if (documentId) {
            parameters = {
                documentId: documentId
            };
        }

        return api.post(EVV_READ_DOCUMENT, { json: parameters })
            .then(response => {
                return Promise.resolve(response);
            })
            .catch(error => {
                return Promise.reject(error);
            });
    }

    updatePdfDocument = async (id, docData) => {
        evvRepository?.evvDb?.document
            .where("id").equals(id)
            .modify(docData);
    }

    createSignaturaInfo = (document) => {
        let signatureInfo = {...document.signature};

        if (signatureInfo.nextStaffId)
            signatureInfo.nextStaffId = signatureInfo.nextStaffId.staffId;

        return signatureInfo;
    }

    createModuleToSave = (document) => {
        const notIncludedFields = ['loaded', 'dbOptions', 'nextClicked', 'isInitializing'];
        let newModuleToSave  = [];

        document.moduleData.forEach(module => {
            if (module.moduleType === "cf") {
                let newModule = {};
                let newFields = [];

                newModule.moduleId = module.moduleId;
                newModule.moduleType = module.moduleType;

                module.fieldData.forEach(fieldData => {
                    if (!notIncludedFields.includes(fieldData.key)) {
                        let newFieldData = {};
                        newFieldData.key = fieldData.key;
                        newFieldData.value = Array.isArray(fieldData.value) ? fieldData.value.map((item) => item && item.length !== 0 ? item : 'null').join("|") : fieldData.value;
                        newFields.push(newFieldData);
                    }
                });

                newModule.fieldData = newFields;
                newModuleToSave.push(newModule);
            } else if (isGA(module)) {
                //Goals Addressed Module
                newModuleToSave = this.getGaModulesToSave(module, newModuleToSave);
            } else {
                //Impact Module
                newModuleToSave = this.getImpactModulesToSave(module, newModuleToSave);
            }
        });

        return newModuleToSave;
    }

    createRequestWriteDocument = (document) => {
        let signatureInfo = this.createSignaturaInfo(document);
        Object.keys(signatureInfo).forEach(
            key => signatureInfo[key] === undefined && delete signatureInfo[key]
        );
        let newModuleToSave  = this.createModuleToSave(document);

        const writeDocument = {
            id: document.id > 0 ? document.id : null,
            serviceDocumentId: document.serviceDocumentId,
            appointmentId: document.appointmentId,
            clientId: document.clientId,
            clientProgramId: document.clientProgramId < 0 ? null : document.clientProgramId,
            staffId: document.staffId,
            organizationId: document.organizationId,
            serviceDate: document.serviceDate.substring(0,10),
            fullySigned: document.fullySigned || false,
            moduleData: newModuleToSave,
            ...(Object.keys(signatureInfo).length > 0 ? { signature: signatureInfo } : {})
        }

        return writeDocument;
    }

    writeDocumentOffline = async (documents, config) => {
        let requestWriteDocuments = documents.map(document => this.createRequestWriteDocument(document));
        requestWriteDocuments = requestWriteDocuments.map(doc => ({
            ...doc,
            online: false
        }));
        const parameters = { resources: requestWriteDocuments };
        
        return api.post(SERVICE_DOCUMENT_WRITE, { json: parameters })
            .then(response => {
                if (response.resources && response.resources.length > 0) {
                    response.resources.forEach((documentFromServer) => {
                        const index = response.resources.indexOf(documentFromServer);
                        const originalDocument = documents[index];

                        documentRepository.findDocumentById(originalDocument.id).then(docById => {
                            const newDoc = {...docById}
                            const store = config.store;
                            
                            if (documentFromServer.statusSync === 'DuplicateKeyException') {
                                syncService.documentService.deleteDocument(docById, {store, doNotClear: true});
                            } else if (documentFromServer.statusSync === 'DataIntegrityViolationException') {
                                syncService.documentService.updateDocumentStatusSync(newDoc.id, documentFromServer.statusSync);
                            } else {
                                syncService.documentService.deleteDocument(docById, {store, doNotClear: true});
                                newDoc.id = response.resources[index].id;
                                newDoc.syncState = "clean";
                                newDoc.clientProgramId = document.clientProgramId;
                                newDoc.organizationId = document.organizationId;
                                newDoc.statusSync = documentFromServer.statusSync;
                                syncService.documentService.saveDocument(newDoc, {store, doNotClear: true});
                            }
                        });
                    });
                }
            })
            .catch(error => {
                const connectionLost = api.verifyIfConnectionLost(error);

                if (connectionLost) {
                    return Promise.resolve(parameters);
                } else {
                    console.log("Error thrown while writing service document: ");
                    console.log(error);
                    return Promise.reject(error);
                }
            });
    }

    sortDocuments = (documents) => {
        return [...sort(documents, ['serviceDate'])];
    };

    writeToServer = (config) => {
        if (config?.skipWrite) {
            return Promise.resolve([]);
        }
        
        console.log('DocumentService.writeToServer - config:');
        console.log(config);
        return documentRepository.getDocumentsSignedAndDirty()
            .then(documents => {
                const documentsSort = this.sortDocuments(documents);
                console.log('Documents in getDocumentsSignedAndDirty() : ' + JSON.stringify(documentsSort));
                if (documentsSort && documentsSort.length > 0) {
                    return this.writeDocumentOffline(documentsSort, config);
                }
            })
            .catch(error => {
                console.log("Error thrown while writing to Documents server: ");
                console.log(error);
            })
    }

    dependentWriteToServerDocuments = async (config, savedAppointments = []) => {
        const documents = await documentRepository.getDocumentsSignedAndDirty();

        if (!documents || documents.length === 0) return [];

        const documentsToProcess = [];
        const savedAppointmentsBySyncId = this.createAppointmentMap(savedAppointments);

        for (const document of documents) {
            try {
                const shouldProcess = await this.processDocument(
                    document,
                    savedAppointmentsBySyncId,
                    documentsToProcess
                );

                if (shouldProcess) {
                    documentsToProcess.push(document);
                }
            } catch (error) {
                console.error(`[dependentWriteToServerDocuments] Error loading appointment for document ${document.id}:`, error);
            }
        }

        return this.finalizeDocumentProcessing(documentsToProcess, config);
    }

    createAppointmentMap = (savedAppointments) => {
        return new Map(
            savedAppointments.filter(a => a.syncId).map(a => [a.syncId, a])
        );
    }

    processDocument = async (document, savedAppointmentsBySyncId, documentsToProcess) => {
        // Standalone documents without appointment are always processed
        if (!document.appointmentId) {
            return true;
        }

        const appointment = await this.findLinkedAppointment(document, savedAppointmentsBySyncId);
        
        if (!appointment) {
            return false;
        }

        const canProcessDocument = this.shouldProcessDocument(appointment);
        
        if (!canProcessDocument) {
            return false;
        }

        await this.updateDocumentIfNeeded(document, appointment, savedAppointmentsBySyncId);
        
        return true;
    }

    findLinkedAppointment = async (document, savedAppointmentsBySyncId) => {
        // Method 1: Search in recently synced appointments (freshest data from server)
        const appointmentFromRecent = this.findAppointmentInRecentlySynced(
            document,
            savedAppointmentsBySyncId
        );
        
        if (appointmentFromRecent) {
            return appointmentFromRecent;
        }

        // Method 2: Search in IndexedDB by appointmentId
        const appointmentById = await this.findAppointmentById(document.appointmentId);
        
        if (appointmentById) {
            return appointmentById;
        }

        // Method 3: Search in IndexedDB by syncId (handles ID changes from negative to positive)
        const appointmentBySyncId = await this.findAppointmentBySyncId(document.syncId);
        
        return appointmentBySyncId;
    }

    findAppointmentInRecentlySynced = (document, savedAppointmentsBySyncId) => {
        if (document.syncId && savedAppointmentsBySyncId.has(document.syncId)) {
            return savedAppointmentsBySyncId.get(document.syncId);
        }
        return null;
    }

    findAppointmentById = async (appointmentId) => {
        return await evvRepository.evvDb.appointment.get(appointmentId);
    }

    findAppointmentBySyncId = async (syncId) => {
        if (!syncId) {
            return null;
        }

        return await evvRepository.evvDb.appointment
            .filter(a => a.syncId === syncId)
            .first();
    }

    shouldProcessDocument = (appointment) => {
        return canProcess(appointment.statusSync) || !appointment.statusSync;
    }

    updateDocumentIfNeeded = async (document, appointment, savedAppointmentsBySyncId) => {
        const shouldUpdateId = this.shouldUpdateDocumentId(
            document,
            appointment,
            savedAppointmentsBySyncId
        );
        
        const shouldClearError = this.shouldClearDocumentError(document);

        if (!shouldUpdateId && !shouldClearError) {
            return;
        }

        const updates = {};

        if (shouldUpdateId) {
            this.prepareIdUpdates(document, appointment, updates);
        }

        if (shouldClearError) {
            this.prepareClearErrorUpdates(document, updates);
        }

        await evvRepository.evvDb.document.update(document.id, updates);
    }

    shouldUpdateDocumentId = (document, appointment, savedAppointmentsBySyncId) => {
        const isAppointmentFromServerResponse = document.syncId && savedAppointmentsBySyncId.has(document.syncId);
        const hasPositiveAppointmentId = appointment.appointmentId > 0;
        const hasNegativeDocumentId = document.appointmentId < 0;

        return isAppointmentFromServerResponse && hasPositiveAppointmentId && hasNegativeDocumentId;
    }

    shouldClearDocumentError = (document) => {
        return document.statusSync === SYNC_STATUS.ERROR;
    }

    prepareIdUpdates = (document, appointment, updates) => {
        // AppointmentId field stores activityDetailId, appointmentIdForQuery stores appointmentId
        document.appointmentId = appointment.activityDetailId || appointment.appointmentId;
        document.appointmentIdForQuery = appointment.appointmentId;
        updates.appointmentId = document.appointmentId;
        updates.appointmentIdForQuery = document.appointmentIdForQuery;
    }

    prepareClearErrorUpdates = (document, updates) => {
        delete document.statusSync;
        updates.statusSync = null;
    }

    finalizeDocumentProcessing = (documentsToProcess, config) => {
        if (documentsToProcess.length === 0) {
            return [];
        }

        const documentsSort = this.sortDocuments(documentsToProcess);
        return this.writeDocumentOffline(documentsSort, config);
    }

    saveDoc = async (id, moduleData, documentStatus, signatures, clientProgramId) => {
        let documentData = {
            "moduleData": moduleData,
            "documentStatus": documentStatus,
            "signature": signatures,
            "clientProgramId": clientProgramId
        }
        evvRepository?.evvDb?.document.where("id").equals(id).modify(documentData);
    }

    writeExtSigNeededDoc = async(parameters) => {
        return api.post(SERVICE_DOCUMENT_WRITE, { json: parameters })
            .then(response => {
                return Promise.resolve(response);
            })
            .catch(error => {
                const connectionLost = api.isConnectionLost(error);

                if (connectionLost){
                    return Promise.resolve(parameters);
                } else {
                    console.log("Error thrown while writing service document: ");
                    console.log(error);
                    return Promise.reject(error);
                }
            });
    }

    addExtSigDocument = async (document, store) => {
        this.saveDocument(document, {store, doNotClear: true})
            .then(savedDocument => {
                console.log(savedDocument, 'addExtSigDocument');
            })
            .catch(error => {
                console.log(error);
            });
    }

    updatingAppointmentDocument = async (document) => {
        try {
            if (document?.appointmentId) {
                const existing = await evvRepository.evvDb.appointmentDocument.where('activityDetailId').equals(document.appointmentId).first();
                if (existing) {
                    await evvRepository.evvDb.appointmentDocument.update(existing.id, {
                        documentId: document.id,
                        documentName: document.documentName,
                        serviceDate: document.serviceDate
                    });
                }
            }
                
            
        } catch (e) {
            console.error('Error saving/updating updatingAppointmentDocument:', e);
        }
    }
    
    updatingAppointmentDocumentPostDB = async (response) => {
        try {
            if (response && response.resources && response.resources.length > 0) {
                for (const doc of response.resources) {
                    if (doc.appointmentId) {
                        const existing = await evvRepository.evvDb.appointmentDocument.where('activityDetailId').equals(doc.appointmentId).first();
                        if (existing) {
                            await evvRepository.evvDb.appointmentDocument.update(existing.id, {
                                documentId: doc.id
                            });
                        }
                    }
                }
            }
        } catch (e) {
            console.error('Error saving/updating updatingAppointmentDocumentPostDB:', e);
        }
    }

    findingDocumentInAppointmentDocument = async (document) => {
        if (!document || !document.id) return null;

        const appointmentDocument = await evvRepository.evvDb.appointmentDocument.where('documentId').equals(document.id).first();
        if (!appointmentDocument) return null;

        const activityId = appointmentDocument.activityId;
        const appointmentDate = appointmentDocument.appointmentDate ? appointmentDocument.appointmentDate.substring(0, 10) : null;
        const clientId = appointmentDocument.clientId;
        const serviceLocationId = appointmentDocument.serviceLocationId;
        const staffId = appointmentDocument.staffId;
        const appointmentType = appointmentDocument.appointmentType;
        const startTime = appointmentDocument.startTime;
        const endTime = appointmentDocument.endTime;
        const organizationId = appointmentDocument.organizationId;
        const programId = appointmentDocument.programId;
        const status = appointmentDocument.status;

        if (!activityId || !appointmentDate || !clientId || !serviceLocationId || !staffId || !appointmentType || !startTime || !endTime || !organizationId || !programId || !status) return null;

        const appointment = await evvRepository.evvDb.appointment
            .where('status').equals(status)
            .and(a =>
                a.activityId === activityId &&
                a.appointmentDate && a.appointmentDate.substring(0, 10) === appointmentDate &&
                a.clientId === clientId &&
                a.serviceLocationId === serviceLocationId &&
                a.staffId === staffId &&
                a.appointmentType === appointmentType &&
                a.startTime === startTime &&
                a.endTime === endTime &&
                a.organizationId === organizationId &&
                a.programId === programId
            )
            .first();

        return appointment || null;
    }

}

export default DocumentService;
